// /agreements/:id — composes the 5 detail sections. Implements:
//   - F-002 eager-parallel credits per assignment (lifted aggregate)
//   - sales_credit_complete derivation for StatusAndTransitions
//   - per-section error isolation
//   - 404 = NotFoundPage (visibility 404 per API §1)

import { useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  agreementsApi,
  assignmentsApi,
  companiesApi,
  creditsApi,
  milestonesApi,
  peopleApi,
} from '@/api/endpoints';
import { qk } from '@/api/queryKeys';
import { usePrincipal } from '@/auth/PrincipalContext';
import { ErrorBanner, Skeleton } from '@/components/primitives';
import { ApiError } from '@/types/errors';
import { AgreementHeader } from '@/components/AgreementHeader';
import { StatusAndTransitions } from '@/components/StatusAndTransitions';
import { AssignmentsSection } from '@/components/AssignmentsSection';
import { PaymentScheduleSection } from '@/components/PaymentScheduleSection';
import { AuditLogSection } from '@/components/AuditLogSection';
import { NotFoundPage } from './NotFoundPage';

export function AgreementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const principal = usePrincipal();
  const qc = useQueryClient();
  const h1Ref = useRef<HTMLHeadingElement | null>(null);

  const agreementQ = useQuery({
    queryKey: qk.agreement(id ?? ''),
    queryFn: () => agreementsApi.get(id!),
    enabled: !!id,
  });

  // Companies + people lookup tables (cheap; cached).
  const companies = useQuery({
    queryKey: qk.companies({ all: true }),
    queryFn: () => companiesApi.list({ limit: 100 }),
    staleTime: 5 * 60_000,
  });
  const people = useQuery({
    queryKey: qk.peopleAll(),
    queryFn: () => peopleApi.list({ limit: 100 }),
    staleTime: 60_000,
  });

  // Assignments + per-assignment eager-parallel credits.
  const assignmentsQ = useQuery({
    queryKey: qk.agreementAssignments(id ?? ''),
    queryFn: () => assignmentsApi.listByAgreement(id!),
    enabled: !!id,
  });
  const assignments = assignmentsQ.data?.data ?? [];

  const creditQueries = useQueries({
    queries: assignments.map((a) => ({
      queryKey: qk.assignmentCredits(a.id),
      queryFn: () => creditsApi.listByAssignment(a.id),
      enabled: !!a.id,
    })),
  });

  // Milestones (FP only).
  const isFP = agreementQ.data?.type === 'fixed_price';
  const milestonesQ = useQuery({
    queryKey: qk.agreementMilestones(id ?? ''),
    queryFn: () => milestonesApi.listByAgreement(id!),
    enabled: !!id && isFP,
  });

  // Derive precondition state for StatusAndTransitions.
  const precondition = useMemo(() => {
    const has_assignments = assignments.length > 0;
    let sales_credit_complete: boolean | null = null;
    const anyCreditPending =
      creditQueries.length > 0 && creditQueries.some((q) => q.isPending);
    const anyCreditError = creditQueries.some((q) => q.isError);
    if (assignmentsQ.isPending) sales_credit_complete = null;
    else if (assignments.length === 0) sales_credit_complete = false;
    else if (anyCreditPending) sales_credit_complete = null;
    else if (anyCreditError) sales_credit_complete = null;
    else {
      sales_credit_complete = creditQueries.every((q) => {
        const credits = q.data?.data ?? [];
        const sales = credits
          .filter((c) => c.credit_type === 'sales')
          .reduce((s, c) => s + c.percentage, 0);
        return sales === 100;
      });
    }

    let payment_schedule_within_tolerance: boolean | null = null;
    if (!isFP) {
      payment_schedule_within_tolerance = true;
    } else if (milestonesQ.isPending) {
      payment_schedule_within_tolerance = null;
    } else if (milestonesQ.isError) {
      payment_schedule_within_tolerance = null;
    } else {
      const ms = milestonesQ.data?.data ?? [];
      const total = ms.reduce(
        (s, m) => s + Math.round(Number(m.amount) * 100),
        0,
      ) / 100;
      const tv = Number(agreementQ.data?.total_value ?? '0');
      payment_schedule_within_tolerance = Math.abs(total - tv) <= 100;
    }

    return {
      has_assignments,
      sales_credit_complete,
      payment_schedule_within_tolerance,
    };
  }, [
    agreementQ.data?.total_value,
    assignments,
    assignmentsQ.isPending,
    creditQueries,
    isFP,
    milestonesQ.data,
    milestonesQ.isError,
    milestonesQ.isPending,
  ]);

  const preconditionError =
    creditQueries.filter((q) => q.isError).length > 1 ||
    (isFP && milestonesQ.isError);

  useEffect(() => {
    if (!agreementQ.isPending) h1Ref.current?.focus();
  }, [agreementQ.isPending]);

  // 404 = NotFoundPage (visibility 404).
  if (agreementQ.error instanceof ApiError && agreementQ.error.status === 404) {
    return <NotFoundPage />;
  }
  if (agreementQ.isError && !(agreementQ.error instanceof ApiError && agreementQ.error.status === 404)) {
    return (
      <ErrorBanner
        message="Couldn't load this agreement."
        requestId={
          agreementQ.error instanceof ApiError
            ? agreementQ.error.requestId
            : undefined
        }
        onRetry={() => void agreementQ.refetch()}
      />
    );
  }
  if (agreementQ.isPending || !agreementQ.data) {
    return (
      <div>
        <h1 ref={h1Ref} tabIndex={-1}>
          Loading agreement…
        </h1>
        <Skeleton rows={6} height="20px" />
      </div>
    );
  }

  const agreement = agreementQ.data;
  const client = companies.data?.data.find((c) => c.id === agreement.client_id);
  const owner = people.data?.data.find((p) => p.id === agreement.owner_person_id);

  const onCreditsChange = () => {
    void qc.invalidateQueries({
      queryKey: qk.agreementAssignments(agreement.id),
    });
  };

  return (
    <div>
      <header className="page-header">
        <h1 ref={h1Ref} tabIndex={-1}>
          {agreement.project_name}
          {agreement.amendment > 0 ? ` — Amendment ${agreement.amendment}` : ''}
          {client ? ` — ${client.name}` : ''}
        </h1>
      </header>

      <AgreementHeader
        agreement={agreement}
        client={client}
        owner={owner}
        principal={principal}
      />
      <StatusAndTransitions
        agreement={agreement}
        principal={principal}
        precondition={precondition}
        preconditionError={preconditionError}
      />
      <AssignmentsSection
        agreement={agreement}
        principal={principal}
        onCreditsChange={onCreditsChange}
      />
      <PaymentScheduleSection agreement={agreement} principal={principal} />
      <AuditLogSection agreementId={agreement.id} />
    </div>
  );
}
